import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'

import Styles from './ManageSubscriptionModal.module.scss'
import { BillingFrequencyRadioButtons } from 'components/Paywall/UpgradePlanModal/components/BillingFrequencyRadioButtons/BillingFrequencyRadioButtons'
import { useEffect, useMemo, useState } from 'react'
import { BillingFrequency, UpgradePlanModal } from 'components/Paywall/UpgradePlanModal/UpgradePlanModal'
import { PlanColumn } from './components/PlanColumn/PlanColumn'
import { useRootStore } from 'stores/RootStore'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { Button } from 'blaze-ui/Button'
import { useCanChangeTier } from 'hooks/useCanChangeTier'
import { DowngradeLimitCheckModal } from 'components/DowngradeLimitCheckModal/DowngradeLimitCheckModal'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { observer } from 'mobx-react-lite'
import { Text } from 'blaze-ui/Text/Text'
import dayjs from 'dayjs'

export const ManageSubscriptionModal = observer(() => {
  const { closeModal, openModal } = useModals()
  const tierUsageStore = useRootStore().tierUsageStore
  const { paymentPlans, currentSubscription, initialized, loading } = tierUsageStore
  const [billingFrequency, setBillingFrequency] = useState<BillingFrequency>(
    currentSubscription?.billing_frequency || 'year',
  )
  const checkCanChangeTier = useCanChangeTier()
  const workspace = useCurrentWorkspace()

  useEffect(() => {
    if (!initialized && !loading && workspace) {
      tierUsageStore.fetch(workspace.id)
    }
  }, [initialized, loading, tierUsageStore, workspace])

  const { creatorPlan, proPlan, teamPlan, agencyPlan } = useMemo(() => {
    const creatorPlan = paymentPlans.find((plan) => plan.key === PaymentPlanKey.BlazeCreator)
    const proPlan = paymentPlans.find((plan) => plan.key === PaymentPlanKey.BlazePro)
    const teamPlan = paymentPlans.find((plan) => plan.key === PaymentPlanKey.BlazeTeam)
    const agencyPlan = paymentPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAgency)
    return { creatorPlan, proPlan, teamPlan, agencyPlan }
  }, [paymentPlans])

  const handlePlanSwitch = async (newPriceKey: string, newPlanKey: PaymentPlanKey) => {
    if (!workspace) {
      showToastMessage(
        'There was an error, please refresh your page and try again. If the issue persists, contact support.',
        'error',
      )
      return
    }

    const canChange = checkCanChangeTier(newPlanKey)

    if (canChange) {
      const res = await WorkspaceClient.updateSubscription(workspace.id, newPriceKey)
      if (res.data.success) {
        const message =
          newPlanKey === currentSubscription?.plan && currentSubscription.cancel_at
            ? 'Your subscription has been reactivated!'
            : `You've switched to the ${res.data.plan_details?.plan} plan!`
        showToastMessage(message, 'success')
        closeModal()
      } else {
        showToastMessage(
          'There was an error, please refresh your page and try again. If the issue persists, contact support.',
          'error',
        )
      }
    } else {
      openModal(DowngradeLimitCheckModal, { downgradePlanTier: newPlanKey })
    }
  }

  if (!currentSubscription) return null

  return (
    <Modal.Root bodyClassName={Styles.wrapperClass} onClose={closeModal} onPressOutside={closeModal}>
      <div className={Styles.headerContainer}>
        <Heading level={3}>Manage Subscription</Heading>
        <div>
          <BillingFrequencyRadioButtons billingFrequency={billingFrequency} setBillingFrequency={setBillingFrequency} />
        </div>
      </div>
      <Modal.Content>
        <div className={Styles.priceGroupColumnsContainer}>
          <div className={Styles.priceGroupColumn}>
            <p className={Styles.priceGroupLabel}>Individual</p>
            <div className={Styles.priceColumnContainer}>
              {!!creatorPlan && (
                <PlanColumn
                  currentPlanKey={currentSubscription.plan}
                  currentPlanCancelAt={currentSubscription.cancel_at}
                  currentBillingFrequency={currentSubscription.billing_frequency}
                  plan={creatorPlan}
                  billingFrequency={billingFrequency}
                  onPlanSwitchClick={(priceKey) => handlePlanSwitch(priceKey, creatorPlan.key)}
                />
              )}
              {!!proPlan && (
                <PlanColumn
                  currentPlanKey={currentSubscription.plan}
                  currentPlanCancelAt={currentSubscription.cancel_at}
                  currentBillingFrequency={currentSubscription.billing_frequency}
                  plan={proPlan}
                  billingFrequency={billingFrequency}
                  onPlanSwitchClick={(priceKey) => handlePlanSwitch(priceKey, proPlan.key)}
                />
              )}
            </div>
            <div className={Styles.infoPanel}>
              <div className={Styles.infoHeader}>
                <div className={Styles.infoLabel}>Enterprise Plan</div>
                <div>Starting at $399/mo annually</div>
              </div>
              <div>
                Get access to advanced security features, personalized training, and invoicing.{' '}
                <a href="mailto:support@blaze.ai">Book a demo</a>
              </div>
            </div>
          </div>
          <div className={Styles.priceGroupColumn}>
            <p className={Styles.priceGroupLabel}>Team</p>
            <div className={Styles.priceColumnContainer}>
              {!!teamPlan && (
                <PlanColumn
                  currentPlanKey={currentSubscription.plan}
                  currentPlanCancelAt={currentSubscription.cancel_at}
                  currentBillingFrequency={currentSubscription.billing_frequency}
                  plan={teamPlan}
                  billingFrequency={billingFrequency}
                  onPlanSwitchClick={(priceKey) => handlePlanSwitch(priceKey, teamPlan.key)}
                />
              )}
              {!!agencyPlan && (
                <PlanColumn
                  currentPlanKey={currentSubscription.plan}
                  currentPlanCancelAt={currentSubscription.cancel_at}
                  currentBillingFrequency={currentSubscription.billing_frequency}
                  plan={agencyPlan}
                  billingFrequency={billingFrequency}
                  onPlanSwitchClick={(priceKey) => handlePlanSwitch(priceKey, agencyPlan.key)}
                />
              )}
            </div>
            <div className={Styles.infoPanel}>
              <div className={Styles.infoHeader}>
                <div className={Styles.infoLabel}>Questions?</div>
                <div></div>
              </div>
              <div>
                Contact anytime at <a href="mailto:support@blaze.ai">support@blaze.ai</a> or hit the question mark at
                the bottom right corner and chat to us via Intercom.
              </div>
            </div>
          </div>
        </div>
      </Modal.Content>

      {currentSubscription.cancel_at && (
        <div className={Styles.cancelledSubscription}>
          <Text variant="primary" color="var(--dark-80)">
            Your subscription is cancelled. You’ll have access to Blaze until{' '}
            {dayjs(currentSubscription.cancel_at).format('ll')} when your subscription ends.
          </Text>
        </div>
      )}
      {!currentSubscription.cancel_at && (
        <Modal.Footer>
          <Modal.FooterContent slot="left">
            <Button
              variant="tertiary"
              onPress={() => {
                openModal(UpgradePlanModal, {
                  initialPage: 'provideReason',
                  onClose: closeModal,
                  showSuccessModal: false,
                })
              }}
            >
              Cancel Subscription
            </Button>
          </Modal.FooterContent>
        </Modal.Footer>
      )}
    </Modal.Root>
  )
})
